import { IS_PRODUCTION, env } from "@/lib/env"
import type { ContentType } from "@prisma/client"
import * as React from "react"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

const RECENT_PAGES_KEY = `tw.recent.pages${IS_PRODUCTION ? "" : `.${env.NEXT_PUBLIC_APP_ENV}`}`

interface BasePage {
  id: string
  type: ContentType
  slug: string
  title: string
  subTitle?: string | null | undefined
  color?: string
  icon?: string | null
  cover?: string | null
}

interface MacrotrendPage extends BasePage {
  type: "MACROTREND"
  icon?: string | null | undefined
}

interface MegatrendPage extends BasePage {
  type: "MEGATREND"
  subTitle: string
  color: string
  cover: string | null
  icon?: string | null
}

interface InnovationPage extends BasePage {
  cover?: string | null
  type: "INNOVATION"
}

interface SubtrendPage extends BasePage {
  type: "SUBTREND"
  subTitle: string
  icon?: string | null
}

interface IndustryPage extends BasePage {
  type: "INDUSTRY"
  icon?: string | null | undefined
}

interface RegionPage extends BasePage {
  type: "REGION"
}
interface ReportPage extends BasePage {
  type: "REPORT"
  cover?: string | null
}

interface StatPage extends BasePage {
  subTitle: string
  type: "STAT"
}

export type RecentPage =
  | MacrotrendPage
  | MegatrendPage
  | InnovationPage
  | SubtrendPage
  | StatPage
  | IndustryPage
  | RegionPage
  | ReportPage

type StoredPages = { pages: RecentPage[]; addPage: (page: RecentPage) => void; clearPages: () => void }

const useStoredPages = create<StoredPages>()(
  persist(
    (set) => ({
      pages: [],
      clearPages: () => set(() => ({ pages: [] })),
      addPage: (page: RecentPage) =>
        set(({ pages }) => ({
          pages: [formatPage(page), ...pages.filter((p) => p.id !== page.id).slice(0, 10)],
        })),
    }),
    { name: RECENT_PAGES_KEY, storage: createJSONStorage(() => window.localStorage) },
  ),
)

function formatPage(page: RecentPage) {
  return {
    id: page.id,
    slug: page.slug,
    title: page.title,
    type: page.type,
    icon: page.icon,
    subTitle: page.subTitle,
    color: page.color,
    cover: page.cover,
  } as RecentPage
}

export function useRecentPages(page?: RecentPage | null | false): [RecentPage[], () => void] {
  const currentPage = React.useRef<RecentPage>(undefined)
  const { pages, addPage, clearPages } = useStoredPages()

  React.useEffect(() => {
    if (!page) return
    if (currentPage.current && page.id === currentPage.current.id) return
    try {
      currentPage.current = page
      addPage(page)
    } catch (error) {
      console.log(error)
    }
  }, [page])

  return [pages, clearPages]
}
